
import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { QWebChannel } from "qwebchannel"
import { useS3Cases, VistCase } from "./useS3Cases"
import { useVistCaseStore } from "../stores/vistCaseStore"

interface VistCaseMetadata {
  version: string
  type: string
  module_id: string
  module_version: string
  case_id: string
  case_name: string
  creation_time: string
  platform_version: string
  discipline_level: string[]
  discipline_tags: string[]
  customer_exclusive: string
  history: any[]
  description: string
  language: string[]
  region: string[]
  guidance: string
}

interface QtWindow {
  qt?: QtBrowser
}

interface QtBrowser {
  webChannelTransport: any
}

interface QtVistCaseManager {
  // Properties
  json: string
  
  // Methods
  refreshAvailableCases(): void
  downloadCase(jobId: string, signedUrl: string): void
  runCase(jobId: string): void
  checkVersionSelectorCompatibility(caseFilePath: string): void
  getDownloadPath(): string
  startScanning(): void
  stopScanning(): void
  setUserId(userId: string): void
  
  // Signals
  jsonChanged: QtSignal<() => void>
  caseDownloadProgress: QtSignal<(jobId: string, percentage: number) => void>
  caseDownloadCompleted: QtSignal<(jobId: string, success: boolean) => void>
  caseRunResult: QtSignal<(jobId: string, success: boolean, error: string) => void>
}

interface QtSignal<F> {
  connect(callback: F): void
  disconnect(callback: F): void
}

interface QtChannel {
  objects: {
    vistCaseManager: QtVistCaseManager
  }
}

interface LocalCase {
  jobId: string
  localPath: string
  lastDownloaded: string
  metadata: any
  isValid: boolean
}

interface QtVistCaseData {
  localCases: LocalCase[]
  availableCases?: VistCase[]
  versionSelectorPath: string
  installationPath: string
  lastVersionCheck?: {
    caseFilePath: string
    matchingInstallations: string[]
    hasCompatibleVersions: boolean
  }
}

interface UseVistCaseManagerReturn {
  // Server cases (from useS3Cases)
  serverCases: VistCase[]
  serverLoading: boolean
  serverError: string | null
  
  // Local cases (from Qt)
  localCases: LocalCase[]
  qtConnected: boolean
  qtError: string | null
  localScanComplete: boolean
  
  // Combined state
  allCases: Array<VistCase & { isLocal?: boolean, localData?: LocalCase }>
  
  // Actions
  downloadCase: (jobId: string) => Promise<void>
  runCase: (jobId: string, sourceUrl: string) => Promise<void>
  refreshAll: () => Promise<void>
  getQtDownloadPath: () => string | null
  startScanning: () => void
  stopScanning: () => void
  
  // Download progress and loading states
  downloadProgress: Record<string, number>
  downloadingCases: Set<string>
  runningCases: Set<string>

  setUserId: (userId: string) => void
}

const connectQt = (): Promise<QtVistCaseManager> => {
  return new Promise((resolve, reject) => {
    const qt = (window as QtWindow).qt || (window.parent as QtWindow).qt
    if (!qt) return reject("Not connected to Qt")

    new QWebChannel(qt.webChannelTransport, (channel: QtChannel) => {
      try {
        const vistCaseManager = channel.objects.vistCaseManager
        if (!vistCaseManager) {
          reject(new Error("vistCaseManager object not found in Qt channel"))
          return
        }
        
        // Add error handling for the channel itself
        const channelAny = channel as any
        if (channelAny.execCallbacks) {
          const originalHandleResponse = channelAny.handleResponse
          channelAny.handleResponse = function(message: any) {
            try {
              if (originalHandleResponse && typeof originalHandleResponse === 'function') {
                return originalHandleResponse.call(this, message)
              }
            } catch (error) {
              console.warn('QWebChannel callback error (handled):', error)
              // Don't rethrow - just log and continue
            }
          }
        }
        
        resolve(vistCaseManager)
      } catch (error) {
        reject(error)
      }
    })
  })
}

export const useVistCaseManager = (): UseVistCaseManagerReturn => {
    // Get state from store
  const store = useVistCaseStore()
  
  const { 
    cases: serverCases, 
    isLoading: serverLoading, 
    error: serverError, 
    fetchCases: fetchServerCases,
    getDownloadUrl
  } = useS3Cases()

  // Sync server cases to store
  useEffect(() => {
    const store = useVistCaseStore.getState()
    store.setServerCases(serverCases)
    store.setServerLoading(serverLoading)
    store.setServerError(serverError)
  }, [serverCases, serverLoading, serverError])

  // Fetch server cases on initialization
  useEffect(() => {
    if (serverCases.length === 0 && !serverLoading) {
      console.log('Fetching server cases on hook initialization')
      fetchServerCases()
    }
  }, [fetchServerCases, serverCases.length, serverLoading])
  
  // Actions that use the global Qt manager
  const downloadCase = useCallback(async (jobId: string) => {
    const qtManager = store.getQtManager()
    if (!qtManager) throw new Error('Qt not connected')
    
    store.addDownloadingCase(jobId)
    try {
      const downloadUrl = await getDownloadUrl(jobId)
      qtManager.downloadCase(jobId, downloadUrl)
    } catch (error) {
      store.removeDownloadingCase(jobId)
      throw error
    }
  }, [getDownloadUrl])

  const runCase = useCallback(async (jobId: string, sourceUrl: string) => {
    const qtManager = store.getQtManager()
    if (!qtManager) throw new Error('Qt not connected')
    
    store.addRunningCase(jobId)
    qtManager.runCase(jobId, sourceUrl)
  }, [])

  const refreshAll = useCallback(async () => {
    await fetchServerCases()
    const qtManager = store.getQtManager()
    if (qtManager) {
      qtManager.refreshAvailableCases()
    }
  }, [fetchServerCases])

  const startScanning = useCallback(() => {
    const qtManager = store.getQtManager()
    if (qtManager) {
      qtManager.startScanning()
    }
  }, [])

  const stopScanning = useCallback(() => {
    const qtManager = store.getQtManager()
    if (qtManager) {
      qtManager.stopScanning()
    }
  }, [])

  const getQtDownloadPath = useCallback((): string | null => {
    const qtManager = store.getQtManager()
    if (!qtManager) return null
    try {
      return qtManager.getDownloadPath()
    } catch (error) {
      console.error('Failed to get Qt download path:', error)
      return null
    }
  }, [])

  const setUserId = useCallback((userId: string) => {
    const qtManager = store.getQtManager()
    if (qtManager) {
      console.log('Setting user ID:', userId)
      qtManager.setUserId(userId)
    }
  }, [])

  return {
    serverCases,
    serverLoading,
    serverError,
    localCases: store.localCases,
    qtConnected: store.qtConnected,
    qtError: store.qtError,
    localScanComplete: store.localScanComplete,
    allCases: store.getCombinedCases(),
    downloadCase,
    runCase,
    refreshAll,
    getQtDownloadPath,
    startScanning,
    stopScanning,
    downloadProgress: store.downloadProgress,
    downloadingCases: store.downloadingCases,
    runningCases: store.runningCases,
    setUserId
  }
}

